/**
 * Leaderboard Manager - Global Leaderboard API Client
 *
 * Handles communication with backend API for global leaderboard
 * - Submit runs
 * - Fetch top runs
 * - Fetch user-specific runs
 * - Get global statistics
 */

import { VictoryRun } from './VictoryHistoryManager';

interface SubmitRunRequest {
  username: string;
  completionTime: number;
  medal: 'gold' | 'silver' | 'bronze';
  collectibles: number;
  totalCollectibles?: number;
}

interface SubmitRunResponse {
  success: boolean;
  rank?: number;
  entry?: VictoryRun;
  error?: string;
}

export interface LeaderboardEntry extends VictoryRun {
  rank: number;
}

interface TopRunsResponse {
  leaderboard: LeaderboardEntry[];
  total: number;
}

interface UserRunsResponse {
  username: string;
  entries: LeaderboardEntry[];
  bestRank: number | null;
  totalRuns: number;
}

interface GlobalStats {
  totalPlayers: number;
  totalRuns: number;
  averageTime: number | null;
  fastestTime: number | null;
  slowestTime: number | null;
  medals: {
    gold: number;
    silver: number;
    bronze: number;
  };
  totalEntries: number;
}

export class LeaderboardManager {
  private static readonly API_BASE = '/api/leaderboard';
  private static readonly TIMEOUT = 10000; // 10 second timeout

  /**
   * Submit run to global leaderboard
   */
  static async submitRun(request: SubmitRunRequest): Promise<SubmitRunResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const response = await fetch(`${this.API_BASE}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Submission failed');
      }

      const result = await response.json();
      return {
        success: true,
        rank: result.rank,
        entry: result.entry,
      };
    } catch (error) {
      console.error('Failed to submit to global leaderboard:', error);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return { success: false, error: 'Request timeout' };
        }
        return { success: false, error: error.message };
      }

      return { success: false, error: 'Network error' };
    }
  }

  /**
   * Get top N entries from global leaderboard
   */
  static async getTopRuns(limit: number = 10): Promise<LeaderboardEntry[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const response = await fetch(`${this.API_BASE}/top?limit=${limit}`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }

      const data: TopRunsResponse = await response.json();
      return data.leaderboard || [];
    } catch (error) {
      console.error('Failed to fetch global leaderboard:', error);
      return [];
    }
  }

  /**
   * Get user's best runs
   */
  static async getUserRuns(username: string): Promise<UserRunsResponse | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const encoded = encodeURIComponent(username);
      const response = await fetch(`${this.API_BASE}/user?username=${encoded}`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch user runs');
      }

      const data: UserRunsResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch user runs:', error);
      return null;
    }
  }

  /**
   * Get global statistics
   */
  static async getStats(): Promise<GlobalStats | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const response = await fetch(`${this.API_BASE}/stats`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data: GlobalStats = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      return null;
    }
  }

  /**
   * Check if API is available
   */
  static async checkConnection(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const response = await fetch(`${this.API_BASE}/stats`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      return response.ok;
    } catch (error) {
      console.error('API connection check failed:', error);
      return false;
    }
  }

  // ============================================
  // Best Heights API Methods
  // ============================================

  /**
   * Submit best height to global leaderboard
   * Minimum height: 20m to reduce spam/low-quality entries
   */
  static async submitBestHeight(
    username: string,
    bestHeight: number,
    collectibles: number = 0,
    totalCollectibles: number = 45
  ): Promise<{ success: boolean; isNewBest: boolean; rank?: number | null }> {
    // Minimum height requirement: 20m
    const MIN_HEIGHT = 20;
    if (bestHeight < MIN_HEIGHT) {
      console.log(
        `⚠️ Height ${bestHeight}m is below minimum (${MIN_HEIGHT}m), not submitting to leaderboard`
      );
      return { success: false, isNewBest: false };
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const response = await fetch('/api/best-heights/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, bestHeight, collectibles, totalCollectibles }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Submission failed');
      }

      const result = await response.json();
      return {
        success: true,
        isNewBest: result.isNewBest,
        rank: result.rank,
      };
    } catch (error) {
      console.error('Failed to submit best height:', error);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return { success: false, isNewBest: false };
        }
      }

      return { success: false, isNewBest: false };
    }
  }

  /**
   * Get top N best heights from leaderboard
   */
  static async getTopBestHeights(limit: number = 50): Promise<BestHeightLeaderboardEntry[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const response = await fetch(`/api/best-heights/top?limit=${limit}`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch best heights leaderboard');
      }

      const data = await response.json();
      return data.leaderboard || [];
    } catch (error) {
      console.error('Failed to fetch best heights leaderboard:', error);
      return [];
    }
  }

  /**
   * Get combined leaderboard (completed runs first, then best heights)
   */
  static async getCombinedLeaderboard(limit: number = 50): Promise<CombinedLeaderboardEntry[]> {
    // Check if online first
    if (!navigator.onLine) {
      throw new Error('OFFLINE');
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const response = await fetch(`/api/leaderboard/combined?limit=${limit}`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch combined leaderboard');
      }

      const data = await response.json();
      return data.leaderboard || [];
    } catch (error) {
      console.error('Failed to fetch combined leaderboard:', error);
      // Re-throw to let caller handle offline state
      throw error;
    }
  }

  /**
   * Get user's rank in combined leaderboard
   */
  static async getUserRank(username: string): Promise<number | null> {
    // Check if online first
    if (!navigator.onLine) {
      throw new Error('OFFLINE');
    }

    try {
      const leaderboard = await this.getCombinedLeaderboard(200); // Fetch more to find user
      const userEntry = leaderboard.find(entry => entry.username === username);
      return userEntry ? userEntry.rank : null;
    } catch (error) {
      console.error('Failed to get user rank:', error);
      // Re-throw to let caller handle offline state
      throw error;
    }
  }

  /**
   * Update username across all leaderboard entries
   */
  static async updateUsername(
    oldUsername: string,
    newUsername: string
  ): Promise<{ success: boolean; totalUpdated: number }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

      const response = await fetch('/api/leaderboard/update-username', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldUsername, newUsername }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Update failed');
      }

      const result = await response.json();
      console.log(
        `✅ Updated ${result.totalUpdated} leaderboard entries: ${oldUsername} -> ${newUsername}`
      );

      return {
        success: true,
        totalUpdated: result.totalUpdated || 0,
      };
    } catch (error) {
      console.error('Failed to update username:', error);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return { success: false, totalUpdated: 0 };
        }
      }

      return { success: false, totalUpdated: 0 };
    }
  }
}

// Export types for best heights
export interface BestHeightLeaderboardEntry {
  id: string;
  username: string;
  bestHeight: number;
  timestamp: number;
  date: string;
  rank: number;
}

// Export types for combined leaderboard
export interface CombinedLeaderboardEntry {
  type: 'completed' | 'height';
  rank: number;
  username: string;

  // For completed runs
  completionTime?: number;
  medal?: 'gold' | 'silver' | 'bronze';
  collectibles?: number;
  totalCollectibles?: number; // Total collectibles in game

  // For best heights
  bestHeight?: number;

  // Common fields
  timestamp: number;
  date: string;
}
